import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { reportService } from "../../../../services/api/reportService";
import { customerService } from "../../../../services/api/customerService";
import { StatusBadge } from "../../../../constants/statusConstants";
import SearchAndFilterBar from "../../components/SearchAndFilterBar";
import CustomSelect from "../../components/CustomSelect";
import CustomTable from "../../components/CustomTable";
import { getBackendUrl } from "../../../../utils/env";
import PieChart from "../../components/PieChart";
import Loading from "../../components/Loading";
import { EmptyChart, hasChartData } from "../../components/EmptyChart";

const toBase64FromPublic = async (path) => {
  try {
    const response = await fetch(path);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image to base64:", error);
    return "";
  }
};

const generateChartImage = async (data, title) => {
  if (!data || data.length === 0) return "";

  const canvas = document.createElement("canvas");
  canvas.width = 800;
  canvas.height = 400;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const chartCenterX = 200;
  const chartCenterY = 200;
  const radius = 140;

  const normalizedData = data.map((item) => ({
    name: item.name || item.label || item.statusName || "N/A",
    value: parseFloat(item.value || item.count || 0),
  }));

  const total = normalizedData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) return "";

  const colors = [
    "#2563EB", "#10B981", "#F97316", "#EF4444", "#8B5CF6",
    "#0EA5E9", "#F59E0B", "#EC4899", "#14B8A6", "#84CC16",
    "#6366F1", "#A855F7",
  ];

  let startAngle = -Math.PI / 2;

  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
  ctx.shadowBlur = 15;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 4;

  normalizedData.forEach((item, index) => {
    const value = item.value;
    const sliceAngle = (value / total) * Math.PI * 2;
    const endAngle = startAngle + sliceAngle;

    const gradient = ctx.createRadialGradient(
      chartCenterX,
      chartCenterY,
      0,
      chartCenterX,
      chartCenterY,
      radius,
    );
    const baseColor = colors[index % colors.length];
    gradient.addColorStop(0, baseColor);
    gradient.addColorStop(1, baseColor + "DD");

    ctx.beginPath();
    ctx.moveTo(chartCenterX, chartCenterY);
    ctx.arc(chartCenterX, chartCenterY, radius, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 3;
    ctx.stroke();

    startAngle = endAngle;
  });

  ctx.restore();

  ctx.beginPath();
  ctx.arc(chartCenterX, chartCenterY, radius * 0.6, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  ctx.textAlign = "center";
  ctx.fillStyle = "#6B7280";
  ctx.font = "400 16px Inter, Arial, sans-serif";
  ctx.fillText("Tổng số", chartCenterX, chartCenterY - 10);

  ctx.fillStyle = "#111827";
  ctx.font = "600 28px Inter, Arial, sans-serif";
  ctx.fillText(total.toLocaleString(), chartCenterX, chartCenterY + 25);

  const legendX = 420;
  const lineHeight = 38;
  const totalLegendHeight = normalizedData.length * lineHeight;
  let legendY = chartCenterY - totalLegendHeight / 2 + 10;

  ctx.textAlign = "left";

  normalizedData.forEach((item, index) => {
    const value = item.value;
    const percentage = ((value / total) * 100).toFixed(1);
    const name = item.name;

    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    ctx.fillStyle = colors[index % colors.length];
    ctx.fillRect(legendX, legendY - 10, 16, 16);
    ctx.restore();

    ctx.strokeStyle = "#E5E7EB";
    ctx.lineWidth = 1;
    ctx.strokeRect(legendX, legendY - 10, 16, 16);

    ctx.fillStyle = "#1F2937";
    ctx.font = "600 14px Inter, Arial, sans-serif";
    ctx.fillText(name, legendX + 24, legendY);

    ctx.font = "400 12px Inter, Arial, sans-serif";
    ctx.fillStyle = "#6B7280";
    ctx.fillText(
      `${value.toLocaleString()} (${percentage}%)`,
      legendX + 24,
      legendY + 16,
    );

    legendY += lineHeight;
  });

  ctx.fillStyle = "#111827";
  ctx.font = "600 16px Inter, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(title, canvas.width / 2, 25);

  return canvas.toDataURL("image/png");
};

const buildPrintHTML = (bodyHTML, title = "Báo cáo") => {
  return `<!doctype html>
<html lang="vi">
<head>
  <meta charset="utf-8"/>
  <title>${title}</title>
  <style>
    :root{--page-w:1123px;--page-h:794px;}
    *{box-sizing:border-box;margin:0;padding:0}
    html,body{background:#fff;color:#111;font-family:Arial,"Times New Roman",serif}
    @page{size:1123px 794px;margin:36px 40px 64px 40px}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .pdf-root{width:100%;line-height:1.35;font-size:13px;padding-bottom:52px}
    table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:12px;table-layout:fixed;page-break-before:always;break-before:page}
    th,td{border:1px solid #d1d5db;padding:6px;word-break:break-word}
    thead{background:#e5e7eb;display:table-header-group}
    .wm-overlay-fixed{
      position:fixed;
      width:100%;
      height:100%;
      top:0;left:0;
      background-size:contain;
      background-repeat:no-repeat;
      background-position:center;
      opacity:0.12;
      pointer-events:none;
      z-index:999999;
    }
    .heading{
      text-align:center;
      font-size:20px;
      font-weight:600;
      color:#0457C2;
      padding-bottom:10px;
      margin-bottom:10px;
      page-break-after:avoid;
    }
    .section-title{
      font-size:16px;
      font-weight:600;
      color:#0457C2;
      margin-top:24px;
      margin-bottom:10px;
      padding-bottom:6px;
      page-break-after:avoid;
    }
    .summary-box{
      display:inline-block;
      background:#f5f5f5;
      border:1px solid #d2d5da;
      border-radius:8px;
      padding:8px 12px;
      margin:4px 6px 4px 0;
      font-size:12px;
    }
    .chart-container{
      text-align:center;
      margin:16px 0;
      page-break-inside:avoid;
    }
    .chart-container img{
      max-width:100%;
      max-height:350px;
      height:auto;
      border-radius:8px;
      padding:10px;
      background:#fff;
      object-fit:contain;
    }
    .report-section{
      page-break-inside:auto;
      break-inside:auto;
      display:block;
      width:100%;
    }
    table{
      page-break-inside:auto;
      break-inside:auto;
    }
    tr {
      page-break-inside:avoid;
      break-inside:avoid;
    }
    .report-footer{
      position:fixed;
      left:40px;
      right:40px;
      bottom:18px;
      text-align:center;
      color:#374151;
      font-size:10.5px;
      line-height:1.35;
    }
  </style>
</head>
<body>
  <div class="wm-overlay-fixed"></div>
  <div class="report-footer">
    <p>Báo cáo được tổng hợp vào ngày ${new Date().toLocaleDateString("vi-VN")} từ Nền tảng số Sohuutritue.vn</p>
    <p>Tổng đài tư vấn Sở hữu trí tuệ: 08 1900 9697 (điện thoại, zalo) - Email: luatsu@sohuutritue.vn</p>
  </div>
  <div class="pdf-root">${bodyHTML}</div>
</body>
</html>`;
};

const ProfileReport = ({ clientId: propClientId }) => {
  const { token } = useContext(AuthContext);

  const [selectedClient, setSelectedClient] = useState(
    propClientId ? { value: propClientId, label: "" } : null,
  );
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerTotalPages, setCustomerTotalPages] = useState(1);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [searchCustomerValue, setSearchCustomerValue] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [profileReport, setProfileReport] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;

  const isEmbedded = !!propClientId;

  const searchDebounceTimer = useRef(null);

  const convertToAPIFormat = (dateStr) => {
    if (!dateStr) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchCustomers = useCallback(
    async (page = 1, search = "", append = false) => {
      if (isEmbedded) return;
      try {
        setLoadingCustomers(true);
        const params = {
          page: page,
          limit: 10,
          search_value: search || undefined,
        };
        const response = await customerService.get_admin_customers_short(
          token,
          params,
        );

        if (response?.success && response?.data) {
          const options = response.data.map((customer) => ({
            value: customer.clientId,
            label: customer.fullName,
          }));

          const allOption = { value: null, label: "-- Tất cả khách hàng --" };

          if (append) {
            setCustomerOptions((prev) => [...prev, ...options]);
          } else {
            setCustomerOptions([allOption, ...options]);
          }

          setCustomerTotalPages(response.totalPages || 1);
        }
      } catch (err) {
        console.error("Lỗi lấy danh sách khách hàng:", err);
        toast.error("Không thể tải danh sách khách hàng");
      } finally {
        setLoadingCustomers(false);
      }
    },
    [token, isEmbedded],
  );

  const handleLoadMoreCustomers = useCallback(() => {
    if (customerPage < customerTotalPages && !loadingCustomers) {
      const nextPage = customerPage + 1;
      setCustomerPage(nextPage);
      fetchCustomers(nextPage, searchCustomerValue, true);
    }
  }, [
    customerPage,
    customerTotalPages,
    loadingCustomers,
    searchCustomerValue,
    fetchCustomers,
  ]);

  const handleSearchCustomer = useCallback(
    (searchValue) => {
      setSearchCustomerValue(searchValue);

      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }

      searchDebounceTimer.current = setTimeout(() => {
        setCustomerPage(1);
        fetchCustomers(1, searchValue, false);
      }, 500);
    },
    [fetchCustomers],
  );

  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  const handleCustomerDropdownOpen = useCallback(() => {
    if (customerOptions.length === 0) {
      fetchCustomers(1, "", false);
    }
  }, [customerOptions.length, fetchCustomers]);

  const fetchReports = useCallback(
    async (page = 1, append = false, isFilter = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else if (isFilter) {
          setTableLoading(true);
        } else {
          setInitialLoading(true);
        }

        const params = {
          clientId:
            selectedClient?.value && selectedClient.value !== null
              ? selectedClient.value
              : undefined,
          fromDate: fromDate ? convertToAPIFormat(fromDate) : undefined,
          toDate: toDate ? convertToAPIFormat(toDate) : undefined,
          page: page,
          limit: pageSize,
        };

        const profileRes = await reportService.get_report_profile(
          token,
          params,
        );

        setProfileReport(profileRes);
        const newData = profileRes?.data || [];

        if (append) {
          setTableData((prev) => [...prev, ...newData]);
        } else {
          setTableData(newData);
        }

        if (profileRes?.pagination) {
          const { page: currPage, totalPages } = profileRes.pagination;
          setHasMore(currPage < totalPages);
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Lỗi lấy báo cáo:", err);
        toast.error("Không thể tải báo cáo");
      } finally {
        setInitialLoading(false);
        setTableLoading(false);
        setLoadingMore(false);
      }
    },
    [token, selectedClient, fromDate, toDate],
  );

  const handleLoadMoreReports = useCallback(() => {
    if (hasMore && !loadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchReports(nextPage, true, false);
    }
  }, [currentPage, hasMore, loadingMore, fetchReports]);

  const handleApplyFilter = () => {
    if (toDate && !fromDate) {
      return toast.error("Vui lòng nhập ngày bắt đầu.");
    }
    if (fromDate && !toDate) {
      return toast.error("Vui lòng nhập ngày kết thúc.");
    }

    const from = fromDate
      ? new Date(fromDate.split("-").reverse().join("-"))
      : null;
    const to = toDate ? new Date(toDate.split("-").reverse().join("-")) : null;

    if (from && to && from > to) {
      return toast.error("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
    }

    setCurrentPage(1);
    fetchReports(1, false, true);
  };

  const handleFromDateChange = (formattedString) => {
    setFromDate(formattedString);
  };

  const handleToDateChange = (formattedString) => {
    setToDate(formattedString);
  };

  const handleClientChange = (option) => {
    setSelectedClient(option);
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      const params = {
        clientId:
          selectedClient?.value && selectedClient.value !== null
            ? selectedClient.value
            : undefined,
        fromDate: fromDate ? convertToAPIFormat(fromDate) : undefined,
        toDate: toDate ? convertToAPIFormat(toDate) : undefined,
      };

      const exportData = await reportService.get_report_profile(token, params);

      const data = exportData?.data || [];
      const summary = exportData?.summary || {};
      const chartData = exportData?.chart || [];

      // Generate chart image
      const chartImage = hasChartData(chartData)
        ? await generateChartImage(chartData, "Biểu đồ hồ sơ")
        : "";

      let bodyHTML = `<h1 class="heading">BÁO CÁO HỒ SƠ</h1>`;

      bodyHTML += `
        <div class="report-section">
          ${selectedClient?.label
          ? `<div style="font-size:12px;color:#374151;margin-bottom:4px">
                <b>Khách hàng:</b> ${selectedClient.label}
              </div>`
          : ""
        }
          ${fromDate || toDate
          ? `<div style="font-size:12px;color:#374151;margin-bottom:10px">
                <b>Thời gian:</b> ${fromDate || "..."} → ${toDate || "..."}
              </div>`
          : ""
        }

          <div style="margin-bottom:12px">
            <div class="summary-box">
              <b>Tổng hồ sơ:</b> ${summary.totalProfiles?.toLocaleString() || 0}
            </div>
            <div class="summary-box">
              <b>Hoàn thành:</b> ${summary.completedProfiles?.toLocaleString() || 0}
            </div>
            <div class="summary-box">
              <b>Chưa hoàn thành:</b> ${summary.unCompletedProfiles?.toLocaleString() || 0}
            </div>
          </div>

          ${chartImage
          ? `<div>
              <div class="chart-container"><img src="${chartImage}" alt="Biểu đồ hồ sơ" /></div>
              <div style="margin-top: 20px;">
                <table>
                  <thead>
                    <tr>
                      <th style="width:50px">STT</th>
                      <th>Tên hồ sơ</th>
                      <th style="width:100px">Mã hồ sơ</th>
                      <th>Dịch vụ</th>
                      <th style="width:100px">Trạng thái</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${data && data.length > 0
            ? data
              .map(
                (item, idx) => `
                        <tr>
                          <td>${idx + 1}</td>
                          <td>${item.profileName || ""}</td>
                          <td>${item.profileCode || "-"}</td>
                          <td>${item.serviceName || ""}</td>
                          <td>${item.status || ""}</td>
                        </tr>
                      `,
              )
              .join("")
            : `<tr><td colspan="5" style="text-align:center;color:#6b7280;padding:16px">Không có dữ liệu</td></tr>`
          }
                  </tbody>
                </table>
              </div>
            </div>`
          : `<div style="margin-top:16px">
              <table>
                <thead>
                  <tr>
                    <th style="width:50px">STT</th>
                    <th>Tên hồ sơ</th>
                    <th style="width:100px">Mã hồ sơ</th>
                    <th>Dịch vụ</th>
                    <th style="width:100px">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  ${data && data.length > 0
            ? data
              .map(
                (item, idx) => `
                      <tr>
                        <td>${idx + 1}</td>
                        <td>${item.profileName || ""}</td>
                        <td>${item.profileCode || "-"}</td>
                        <td>${item.serviceName || ""}</td>
                        <td>${item.status || ""}</td>
                      </tr>
                    `,
              )
              .join("")
            : `<tr><td colspan="5" style="text-align:center;color:#6b7280;padding:16px">Không có dữ liệu</td></tr>`
          }
                </tbody>
              </table>
            </div>`
        }
        </div>
      `;

      const html = buildPrintHTML(bodyHTML, "Báo cáo hồ sơ");
      const watermarkBase64 = await toBase64FromPublic(
        "/images/watermark-45.png",
      );
      const BE_URL = getBackendUrl();

      const response = await fetch(`${BE_URL}/pdf/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          html,
          profileName: "Báo cáo hồ sơ",
          watermarkBase64,
          watermarkOpacity: 0.12,
          options: { width: 1123, height: 794, deviceScaleFactor: 2 },
          watermarkMode: "fixed",
        }),
      });

      if (!response.ok) throw new Error("Không thể tạo PDF");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bao-cao-ho-so-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Xuất báo cáo thành công!");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Xuất PDF thất bại");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchReports(1, false, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (initialLoading || exporting) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 flex justify-between items-center mb-6">
        {!isEmbedded && <h1 className="text-2xl font-semibold text-gray-800">Báo cáo hồ sơ</h1>}

        {!isEmbedded && (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-default flex items-center gap-2"
          >
            {exporting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Đang xuất...
              </>
            ) : (
              <>
                Xuất file
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.66667 6.66667L8 10L11.3333 6.66667"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 10V2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            )}
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-visible">
        <div
          className={`${!isEmbedded ? "bg-white rounded-[16px] border border-[#D2D5DA] shadow-sm p-4" : ""} flex flex-col gap-4 h-full min-h-0 overflow-visible`}
        >
          <div className="shrink-0 flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold">Báo cáo hồ sơ</h2>
            </div>

            <div className="flex flex-row justify-center items-center gap-3">
              {!isEmbedded && (
                <div className="w-[290px]">
                  <CustomSelect
                    options={customerOptions}
                    placeholder="Chọn khách hàng"
                    value={selectedClient}
                    onChange={handleClientChange}
                    searchable={true}
                    height="42px"
                    infinite={true}
                    loadMore={handleLoadMoreCustomers}
                    hasMore={customerPage < customerTotalPages}
                    loadingMore={loadingCustomers}
                    onSearch={handleSearchCustomer}
                    onDropdownOpen={handleCustomerDropdownOpen}
                    controlClassName="rounded-lg"
                    dropdownClassName="rounded-lg"
                  />
                </div>
              )}

              <SearchAndFilterBar
                showSearchInput={false}
                onSearchClick={handleApplyFilter}
                showDatePickers={true}
                fromDatePlaceholder="Từ ngày"
                fromDateValue={fromDate}
                toDatePlaceholder="Đến ngày"
                toDateValue={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
                showFilter={true}
                filterLabel="Áp dụng"
                showAdditionBtn={false}
              />

              {isEmbedded && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-blue-600 text-white px-3 py-[9px] rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-sm flex items-center gap-2 h-[42px]"
                >
                  {exporting ? (
                    "Đang xuất..."
                  ) : (
                    <>
                      Xuất file
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4.66667 6.66667L8 10L11.3333 6.66667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 10V2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="shrink-0 flex flex-wrap gap-4">
            <div className="bg-[#F9F9F9] border border-[#D2D5DA] inline-flex px-6 py-4 rounded-[16px]">
              <span className="text-gray-600">Tổng số hồ sơ:&nbsp;</span>
              <span className="font-semibold">
                {profileReport?.summary?.totalProfiles || 0}
              </span>
            </div>

            <div className="bg-[#F9F9F9] border border-[#D2D5DA] inline-flex px-6 py-4 rounded-[16px]">
              <span className="text-gray-600">Hồ sơ hoàn thành:&nbsp;</span>
              <span className="font-semibold">
                {profileReport?.summary?.completedProfiles || 0}
              </span>
            </div>

            <div className="bg-[#F9F9F9] border border-[#D2D5DA] inline-flex px-6 py-4 rounded-[16px]">
              <span className="text-gray-600">Hồ sơ chưa hoàn thành:&nbsp;</span>
              <span className="font-semibold">
                {profileReport?.summary?.unCompletedProfiles || 0}
              </span>
            </div>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-3 gap-4">
            <div className="rounded-[12px] border border-[#E3E3E3] p-4 w-full overflow-visible">
              <div className="flex items-center gap-8 w-full">
                <div className="flex-shrink-0 w-full">
                  {hasChartData(profileReport?.chart) ? (
                    <PieChart
                      data={profileReport?.chart || []}
                      size={280}
                      legendPosition="bottom"
                    />
                  ) : (
                    <EmptyChart title="Chưa có dữ liệu hồ sơ" />
                  )}
                </div>
              </div>
            </div>

            <div className="col-span-2 flex flex-col min-h-0 overflow-visible">
              <div className="flex-1 min-h-0 overflow-visible">
                <div className="h-full min-h-0 overflow-auto p-[2px]">
                  <CustomTable
                    columns={[
                      {
                        key: "index",
                        title: "STT",
                        align: "text-left",
                        render: (_, __, idx) => idx + 1,
                      },
                      {
                        key: "profileName",
                        title: "TÊN HỒ SƠ",
                        align: "text-left",
                      },
                      {
                        key: "profileCode",
                        title: "MÃ HỒ SƠ",
                        align: "text-left",
                        render: (value) => value || "-",
                      },
                      {
                        key: "serviceName",
                        title: "LOẠI DỊCH VỤ",
                        align: "text-left",
                      },
                      {
                        key: "status",
                        title: "TRẠNG THÁI",
                        align: "text-right",
                        render: (value) => <StatusBadge status={value} />,
                      },
                    ]}
                    data={tableData}
                    setView={false}
                    setEdit={false}
                    setDelete={false}
                    infinite={true}
                    loadMore={handleLoadMoreReports}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    loading={tableLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileReport;
